/**
 * Botones de los turnos de WORKFLOW que llegan por el stream (SSE).
 *
 * 🔴 Un turno de workflow manda un unico `final_answer` con el texto y, aparte,
 * `ui_components: [{type:"buttons", buttons:[{title,payload}]}]`. El widget solo
 * pintaba el texto: los botones dependian de que PHP insertara su fila aparte
 * ("👇" + `quick_replies`) y de que el INSERT llegara por realtime. Si el realtime
 * no entregaba, el usuario veia la pregunta sin los botones Si/No (chat
 * 4553fd4c-6345-44cf-a62d-de7b87ee1f4d, 2026-10-30, Beds Speakap).
 *
 * Ahora el stream los pinta como placeholder local (`__local_buttons`) al final.
 * Si llegan las filas canonicas (realtime o reconciliacion), el placeholder es
 * TRANSPARENTE para la insercion ordenada —las imagenes, documentos o carruseles
 * del mismo turno se colocan delante de el— y la fila de botones lo quita y entra
 * en su sitio por `created_at`. Asi el orden final es el mismo que sin stream
 * (p.ej. texto > imagen > texto > botones).
 *
 * Solo workflows a proposito: en los turnos del LLM PHP puede descartar los
 * quick_replies (mensaje de calificacion, `dropAnswerQuickreplies`) y los
 * carruseles salen de las fuentes, asi que pintarlos desde el stream mostraria
 * botones que nunca se persisten.
 */

// Texto que PHP pone a la fila de quick_replies (`handleQuickreplies`). Se usa el
// mismo para que la sustitucion por la fila canonica no se note.
export const BUTTONS_TEXT = '👇';

// Tope de `handleprocessQuickreplies` en PHP: con mas botones, la fila canonica
// no coincidiria con el placeholder y saldrian duplicados.
const MAX_QUICK_REPLIES = 13;

/**
 * quick_replies listos para `buildMensajeWeb` a partir del `final_answer` de un
 * turno de workflow, o null si no es workflow o no trae botones.
 */
export function workflowQuickReplies(payload) {
    if (!payload || payload.type !== 'final_answer' || !payload.workflow) return null;
    const ui = Array.isArray(payload.ui_components) ? payload.ui_components : [];
    const quickReplies = [];
    for (const component of ui) {
        if (!component || component.type !== 'buttons' || !Array.isArray(component.buttons)) continue;
        for (const b of component.buttons) {
            const title = String(typeof b === 'string' ? b : (b?.title ?? b?.label ?? '')).trim();
            if (!title) continue;
            const value = typeof b === 'string' ? b : (b?.payload ?? b?.value ?? title);
            quickReplies.push({ title, payload: String(value), content_type: 'text' });
        }
    }
    return quickReplies.length > 0 ? quickReplies.slice(0, MAX_QUICK_REPLIES) : null;
}

// Clave por los titulos visibles: es lo que comparten el stream y la fila de PHP.
export function quickRepliesKey(quickReplies) {
    return JSON.stringify(quickReplies.map((q) => String(q?.title ?? '').trim()));
}

/**
 * Clave de una fila de `messages` (su `content.message`) si es SOLO botones
 * quick_replies, o null. Carruseles, tarjetas, adjuntos y botones de plantilla
 * nunca cuentan: esos no los pinta el stream y no deben consumir el placeholder.
 */
export function rowButtonsKey(message) {
    if (!message || typeof message !== 'object' || Array.isArray(message)) return null;
    if (!Array.isArray(message.quick_replies) || message.quick_replies.length === 0) return null;
    if (message.cards || message.attachment || message.attachments?.length || message.buttons?.length) return null;
    return quickRepliesKey(message.quick_replies);
}

/**
 * Si la fila canonica corresponde a un placeholder de botones del stream, lo
 * QUITA y devuelve su indice (-1 si no hay). La fila se inserta luego en orden
 * con `posicionOrdenada`. Si hay varios con la misma clave (re-pregunta del nodo
 * con los mismos botones sin realtime) se quita el ultimo: es el del turno en curso.
 */
export function removeLocalButtons(messages, rowMessage) {
    const key = rowButtonsKey(rowMessage);
    if (key === null) return -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i]?.__local_buttons === key) {
            messages.splice(i, 1);
            return i;
        }
    }
    return -1;
}

/**
 * Indice donde insertar un mensaje del bot con timestamp `t`: recorre desde el
 * final y sube mientras encuentre mensajes del servidor mas nuevos; se detiene en
 * el primer mensaje local (sin id) para no adelantar mensajes del usuario. Los
 * placeholders de botones se saltan: van siempre detras de las filas de su turno.
 */
export function posicionOrdenada(messages, t, toTimestamp) {
    let i = messages.length;
    while (i > 0) {
        const prev = messages[i - 1];
        if (prev.__local_buttons) { i--; continue; }
        if (!prev.id) break;                          // mensaje local (usuario)
        if (toTimestamp(prev.created_at) <= t) break; // ya esta ordenado
        i--;
    }
    return i;
}
